import React from 'react';
import { useWorldStore } from '../../state/worldStore';
import { SLICE_DISTRICT } from '../../config';
import { localHour } from '../../worldTime';
import { useCallerFace, faceDiscStyle, faceImgStyle } from '../../hooks/useCallerFace';
import PhoneCalls from './PhoneCalls';
import PhoneRides from './PhoneRides';
import PhoneLyra from './PhoneLyra';
import PhoneContacts from './PhoneContacts';
import PhoneRecords from './PhoneRecords';
import PhoneVideo from './PhoneVideo';

// PhoneScreen — the device itself, with NO opinion about where it is.
//
// Split out because the phone has two homes and one identity. In THIRD person
// you are looking at your character, so the phone belongs to you and lives in
// the HUD. In FIRST person you are looking through their eyes, so the phone
// belongs to THEM and belongs in their hand — the same screen, rendered in the
// world instead of over it.
//
// Everything stateful lives in the store, so both placements paint the same
// device and a call survives moving between them.
export { APPS };

const APPS = [
  { key: 'calls', label: 'Calls', icon: '📞', accent: '#7dff9d' },
  { key: 'contacts', label: 'Contacts', icon: '👥', accent: '#7dd3ff' },
  // Person-to-person video. Distinct from Contacts (which calls ARTISTS — the
  // Anam avatars): this one calls a MEMBER, and the person who answers is a
  // person.
  { key: 'video', label: 'Video', icon: '📹', accent: '#7dff9d' },
  { key: 'records', label: 'Records', icon: '📀', accent: '#ffb25e' },
  { key: 'rides', label: 'Rides', icon: '🚕', accent: '#ffd27d' },
  { key: 'lyra', label: 'Lyra', icon: '📋', accent: '#ff9dc4' },
];

// The rest of treatment §3's table. Shown, dimmed, because a phone with three
// icons reads as broken while a phone with three live icons and five coming
// reads as early — and because each of these already exists elsewhere in
// HypeTunes and is a port, not an invention.
const SOON = [
  { key: 'music', label: 'Music', icon: '🎵' },
  { key: 'map', label: 'Map', icon: '🗺' },
  { key: 'camera', label: 'Camera', icon: '📷' },
  { key: 'feed', label: 'Feed', icon: '📰' },
  { key: 'wallet', label: 'Wallet', icon: '💳' },
];


export default function PhoneScreen({ onToggle }) {
  const phoneApp = useWorldStore((s) => s.phoneApp);
  const setPhoneApp = useWorldStore((s) => s.setPhoneApp);
  const incomingCall = useWorldStore((s) => s.incomingCall);
  const missedCalls = useWorldStore((s) => s.missedCalls);
  const phoneCall = useWorldStore((s) => s.phoneCall);
  const scene = useWorldStore((s) => s.scene);
  const quests = useWorldStore((s) => s.phoneQuests);
  const refresh = useWorldStore((s) => s.refreshQuests);

  const clock = (() => {
    const h = localHour(SLICE_DISTRICT.timezone);
    const hh = Math.floor(h);
    const mm = Math.floor((h - hh) * 60);
    const ampm = hh >= 12 ? 'PM' : 'AM';
    const h12 = hh % 12 === 0 ? 12 : hh % 12;
    return `${h12}:${String(mm).padStart(2, '0')} ${ampm}`;
  })();

  const badge = (incomingCall ? 1 : 0) + missedCalls.length;
  const inCall = phoneCall && !!scene;
  // The bar is suppressed while ringing or mid-call: those screens are the whole
  // phone, and a call must not sit one mis-tap from being abandoned.
  const showAppBar = !!phoneApp && !incomingCall && !inCall;
  const appLabel = APPS.find((a) => a.key === phoneApp)?.label || '';
  const togglePhone = onToggle || (() => {});

  return (
    <div
    className="ht-phone"
    style={{
      width: '100%', height: '100%', boxSizing: 'border-box', borderRadius: 34, padding: 5,
      background: 'linear-gradient(150deg,#25232a,#0a090c 40%,#17151b)',
      boxShadow: '0 24px 70px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.09)',
      fontFamily: 'system-ui, sans-serif',
      animation: incomingCall ? 'ht-ring 1.6s ease-in-out infinite' : 'none',
      transformOrigin: 'bottom center',
    }}
  >
    <div style={{
      height: '100%', borderRadius: 29, overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
      background: 'radial-gradient(120% 60% at 50% 0%, #3a2318 0%, #1a1114 45%, #0d0a10 100%)',
      color: '#ffe9c4',
    }}>
      {/* Status bar doubles as the grab handle: when the phone is pocketed
          this strip is the only thing showing, so it carries the badge. */}
      <button
        onClick={togglePhone}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '13px 18px 9px', height: 46, boxSizing: 'border-box',
          background: 'none', border: 'none', color: 'inherit', cursor: 'pointer',
          font: '700 11.5px system-ui', letterSpacing: 0.4, width: '100%',
        }}
        aria-label="Pocket the phone"
      >
        <span style={{ opacity: 0.9 }}>{clock}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: 0.75 }}>
            {badge > 0 && (
              <span style={{
                background: '#ff5e78', color: '#150a0c', borderRadius: 999,
                padding: '1px 6px', font: '800 10px system-ui',
              }}>{badge}</span>
            )}
            HypePhone ▮▮▮
          </span>
          {/* The way OUT, visible (Khari: "closing it isn't intuitive").
              A span, not a nested button — the whole bar is already the
              pocket control; this just makes that legible. */}
          <span aria-hidden="true" style={{
            width: 21, height: 21, borderRadius: '50%', display: 'grid', placeItems: 'center',
            border: '1px solid rgba(255,233,196,0.4)', background: 'rgba(255,233,196,0.09)',
            font: '700 10.5px system-ui', opacity: 0.85, lineHeight: 1,
          }}>✕</span>
        </span>
      </button>

      {/* App bar. A real phone's home gesture has a decade of muscle memory
          behind it; a 4px line in a web overlay has none, so the way back is
          a LABELLED control and the pill is only a second route to it. */}
      {showAppBar && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '2px 10px 8px',
          borderBottom: '1px solid rgba(255,233,196,0.1)',
        }}>
          <button onClick={() => setPhoneApp(null)} style={backBtn} aria-label="Back to home screen">
            <span style={{ fontSize: 15, lineHeight: 1 }}>‹</span> Home
          </button>
          <span style={{ marginLeft: 'auto', font: '800 15px system-ui' }}>{appLabel}</span>
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {incomingCall ? (
          <IncomingCall />
        ) : inCall || phoneApp === 'calls' ? (
          <PhoneCalls onQuestChange={refresh} />
        ) : phoneApp === 'video' ? (
          <PhoneVideo />
        ) : phoneApp === 'contacts' ? (
          <PhoneContacts />
        ) : phoneApp === 'rides' ? (
          <PhoneRides />
        ) : phoneApp === 'lyra' ? (
          <PhoneLyra quests={quests} onRefresh={refresh} />
        ) : (
          <Home badge={badge} onOpen={setPhoneApp} />
        )}
      </div>

      {/* Home bar. Hidden mid-call so a call is never one stray tap from
          gone, and inert on the home screen so it can't read as broken. */}
      {!incomingCall && !inCall && (
        <button
          onClick={() => setPhoneApp(null)}
          disabled={!phoneApp}
          style={{
            border: 'none', background: 'none', cursor: phoneApp ? 'pointer' : 'default',
            padding: '10px 0 13px', display: 'flex', justifyContent: 'center',
          }}
          aria-label="Home"
        >
          <span style={{
            width: 96, height: 4, borderRadius: 999,
            background: phoneApp ? 'rgba(255,233,196,0.55)' : 'rgba(255,233,196,0.22)',
          }} />
        </button>
      )}
    </div>
  </div>
  );
}

// ── Home ────────────────────────────────────────────────────────────────────
function Home({ badge, onOpen }) {
  // Selector, not getState(): this badge has to repaint the moment a track lands.
  const alerts = useWorldStore((s) => s.recordAlerts.length);
  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '14px 16px' }}>
      <div style={{ font: '800 22px system-ui', letterSpacing: -0.3, marginBottom: 2 }}>
        {SLICE_DISTRICT.name}
      </div>
      <div style={{ font: '600 12px system-ui', opacity: 0.5, marginBottom: 18 }}>
        {SLICE_DISTRICT.city} · you're on the ground
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
        {APPS.map((a) => (
          <button key={a.key} onClick={() => onOpen(a.key)} style={appTile(a.accent)}>
            <span style={{ fontSize: 24, lineHeight: 1 }}>{a.icon}</span>
            <span style={{ font: '700 10.5px system-ui' }}>{a.label}</span>
            {((a.key === 'calls' && badge > 0) || (a.key === 'records' && alerts > 0)) && (
              <span style={{
                position: 'absolute', top: -5, right: -5, background: '#ff5e78', color: '#150a0c',
                borderRadius: 999, minWidth: 18, padding: '1px 5px', font: '800 10px system-ui',
              }}>{a.key === 'records' ? alerts : badge}</span>
            )}
          </button>
        ))}
        {SOON.map((a) => (
          <div key={a.key} style={{ ...appTile('rgba(255,233,196,0.18)'), opacity: 0.32, cursor: 'default' }}>
            <span style={{ fontSize: 24, lineHeight: 1, filter: 'grayscale(1)' }}>{a.icon}</span>
            <span style={{ font: '700 10.5px system-ui' }}>{a.label}</span>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 16, font: '600 10.5px system-ui', opacity: 0.4, lineHeight: 1.5 }}>
        Music, Map, Camera, Feed and Wallet already exist on HypeTunes — they land
        here as ports, not as new screens.
      </div>
    </div>
  );
}

const backBtn = {
  display: 'flex', alignItems: 'center', gap: 3,
  padding: '6px 10px', borderRadius: 999, cursor: 'pointer',
  border: '1px solid rgba(255,178,94,0.35)', background: 'rgba(255,178,94,0.12)',
  color: '#ffb25e', font: '700 11.5px system-ui',
};

const appTile = (accent) => ({
  position: 'relative',
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 7,
  aspectRatio: '1 / 1', borderRadius: 17, cursor: 'pointer',
  border: `1px solid ${accent}`, background: 'rgba(255,255,255,0.055)',
  color: '#ffe9c4', textAlign: 'center', padding: 4,
});

// ── The ring ────────────────────────────────────────────────────────────────
function IncomingCall() {
  const incomingCall = useWorldStore((s) => s.incomingCall);
  const answerCall = useWorldStore((s) => s.answerCall);
  const declineCall = useWorldStore((s) => s.declineCall);
  // Her FACE rings, not her initial — fetched by sceneKey so the photo is the
  // same avatar whose voice answers. Falls back to the initial disc.
  const face = useCallerFace(incomingCall?.sceneKey || null);
  if (!incomingCall) return null;

  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'space-between', padding: '34px 20px 30px', textAlign: 'center',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
        <div className="ht-ringing" style={{
          ...faceDiscStyle(92),
          animation: 'ht-pulse 1.8s ease-out infinite',
        }}>
          {face?.image
            ? <img src={face.image} alt="" style={faceImgStyle} />
            : incomingCall.from.charAt(0)}
        </div>
        <div>
          <div style={{ font: '800 22px system-ui' }}>{incomingCall.from}</div>
          <div style={{ font: '600 12px system-ui', opacity: 0.6, marginTop: 3 }}>
            {incomingCall.role} · mobile
          </div>
        </div>
        <div style={{ font: '600 12.5px system-ui', opacity: 0.5 }}>incoming call…</div>
      </div>

      {/* NOT onClick={answerCall}: React hands a handler the click event as its
          first argument, and answerCall takes an optional call to return — so a
          PointerEvent arrived where a call was expected and the scene opened
          with sceneKey undefined. Same shape as the begin() bug in SceneDock. */}
      <div style={{ display: 'flex', gap: 34 }}>
        <button onClick={() => declineCall()} style={callBtn('#ff5065')} aria-label="Decline">✕</button>
        <button onClick={() => answerCall()} className="ht-answer" style={{ ...callBtn('#2fd06a'), animation: 'ht-pulse 1.8s ease-out infinite' }} aria-label="Answer">📞</button>
      </div>
    </div>
  );
}

const callBtn = (bg) => ({
  width: 62, height: 62, borderRadius: '50%', border: 'none', cursor: 'pointer',
  background: bg, color: '#fff', fontSize: 22, display: 'grid', placeItems: 'center',
  boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
});
